import * as tf from '@tensorflow/tfjs';
import * as faceDetection from '@tensorflow-models/face-detection';
import { LoggerService } from './LoggerService';
import store from './store';
import { Browser, MobileOS } from '../models/IDevice';

/**
 * Enhanced ModelLoadingService
 * Supports multiple models: ID detection + face detection
 */
class ModelLoadingService {
  // Backend state
  private backendReady = false;
  private backendPromise: Promise<void> | null = null;

  // ID Detection Model (your existing model)
  private idDetectionModel: any | null = null;
  private idModelReady = false;
  private idModelPromise: Promise<void> | null = null;

  // Face Detection Model (new)
  private faceDetector: faceDetection.FaceDetector | null = null;
  private faceModelReady = false;
  private faceModelPromise: Promise<void> | null = null;

  /**
   * Initialize TensorFlow.js backend
   * This MUST be called before loading any models
   */
  public async initializeBackend(): Promise<void> {
    if (this.backendReady) {
      LoggerService.logMulti('Backend already initialized');
      return;
    }

    if (this.backendPromise) {
      LoggerService.logMulti('Backend initialization in progress, waiting...');
      return this.backendPromise;
    }

    this.backendPromise = this._initBackend();
    await this.backendPromise;
  }

  private async _initBackend(): Promise<void> {
    try {
      LoggerService.logMulti('Initializing TensorFlow.js backend...');

      const isSafari = store.device.browser.name === Browser.Safari;
      const isAppleOS = store.device.os.name === MobileOS.iOS || store.device.os.name === MobileOS.MacOS;

      if (isSafari && isAppleOS) {
        LoggerService.logMulti('Apple device detected - using CPU backend');
        await tf.setBackend('cpu');
      } else {
        LoggerService.logMulti('Using WebGL backend');
        await tf.setBackend('webgl');
      }

      await tf.ready();
      this.backendReady = true;

      LoggerService.logMulti(`✅ TensorFlow.js backend ready: ${tf.getBackend()}`);
      this.logMemory();
    } catch (error) {
      LoggerService.warn('Backend initialization failed:', error);

      // Fallback to CPU
      try {
        LoggerService.logMulti('Falling back to CPU backend...');
        await tf.setBackend('cpu');
        await tf.ready();
        this.backendReady = true;
        LoggerService.logMulti('✅ CPU backend ready');
      } catch (fallbackError) {
        LoggerService.warn('CPU fallback also failed:', fallbackError);
        throw fallbackError;
      }
    } finally {
      this.backendPromise = null;
    }
  }

  /**
   * Initialize everything (backward compatible with your existing code)
   */
  public async initialize(): Promise<void> {
    await this.initializeBackend();
    //await this.loadIDDetectionModel();
    await this.initializeAll();
  }

  /**
   * Non-blocking init (backward compatible)
   */
  public init(): void {
    this.initialize()
      .then(() => {
        LoggerService.logMulti('Models initialized');
      })
      .catch(error => {
        LoggerService.warn('Model initialization failed:', error);
      });
  }

  /**
   * Load ID Detection Model (your existing model)
   */
  public async loadIDDetectionModel(): Promise<void> {
    if (this.idModelReady) {
      LoggerService.logMulti('ID model already loaded');
      return;
    }

    if (this.idModelPromise) {
      LoggerService.logMulti('ID model loading in progress, waiting...');
      return this.idModelPromise;
    }

    // Ensure backend is ready first
    await this.initializeBackend();

    this.idModelPromise = this._loadIDModel();
    await this.idModelPromise;
  }

  private async _loadIDModel(): Promise<void> {
    try {
      LoggerService.logMulti('Loading ID detection model...');

      this.idDetectionModel = await tf.loadGraphModel(store.idModelsPath);
      this.idModelReady = true;

      LoggerService.logMulti('✅ ID detection model loaded');
      this.logMemory();
    } catch (error) {
      LoggerService.warn('ID model load failed, retrying...', error);

      // Retry once
      try {
        this.idDetectionModel = await tf.loadGraphModel(store.idModelsPath);
        this.idModelReady = true;
        LoggerService.logMulti('✅ ID model loaded on retry');
      } catch (retryError) {
        LoggerService.warn('ID model retry failed:', retryError);
        throw retryError;
      }
    } finally {
      this.idModelPromise = null;
    }
  }

  /**
   * Load Face Detection Model
   */
  public async loadFaceDetectionModel(): Promise<void> {
    if (this.faceModelReady) {
      LoggerService.logMulti('Face detection model already loaded');
      return;
    }

    if (this.faceModelPromise) {
      LoggerService.logMulti('Face model loading in progress, waiting...');
      return this.faceModelPromise;
    }

    // Ensure backend is ready first
    await this.initializeBackend();

    this.faceModelPromise = this._loadFaceModel();
    await this.faceModelPromise;
  }

  private async _loadFaceModel(): Promise<void> {
    try {
      LoggerService.logMulti('Loading face detection model...');

      const model = faceDetection.SupportedModels.MediaPipeFaceDetector;

      // Check if running in WebView
      const isWebView = window.location.protocol === 'file:';

      let detectorConfig;

      if (isWebView || !store.faceModelsPath) {
        // Use tfjs runtime for WebView or if no Azure path configured
        LoggerService.logMulti('Using tfjs runtime (no external files)');
        detectorConfig = {
          runtime: 'tfjs' as const,
          maxFaces: 1,
        };
      } else {
        // Use MediaPipe with Azure for web (if files are uploaded)
        LoggerService.logMulti(`Using mediapipe runtime from: ${store.faceModelsPath}`);
        detectorConfig = {
          runtime: 'mediapipe' as const,
          solutionPath: store.faceModelsPath,
          modelType: 'short' as const,
          maxFaces: 1,
        };
      }

      this.faceDetector = await faceDetection.createDetector(model, detectorConfig);
      this.faceModelReady = true;

      LoggerService.logMulti('✅ Face detection model loaded');
      this.logMemory();
    } catch (error) {
      LoggerService.warn('Face model load failed:', error);

      // Fallback to tfjs if mediapipe fails
      if (error.message?.includes('file://') || error.message?.includes('Failed to fetch')) {
        LoggerService.logMulti('Falling back to tfjs runtime...');
        try {
          const fallbackConfig = {
            runtime: 'tfjs' as const,
            maxFaces: 1,
          };
          this.faceDetector = await faceDetection.createDetector(faceDetection.SupportedModels.MediaPipeFaceDetector, fallbackConfig);
          this.faceModelReady = true;
          LoggerService.logMulti('✅ Face detection loaded with tfjs fallback');
          return;
        } catch (fallbackError) {
          LoggerService.error('Fallback also failed:', fallbackError);
        }
      }

      throw error;
    } finally {
      this.faceModelPromise = null;
    }
  }

  /**
   * Initialize both models at once
   */
  public async initializeAll(): Promise<void> {
    await this.initializeBackend();

    // Load both models in parallel
    await Promise.all([this.loadIDDetectionModel(), this.loadFaceDetectionModel()]);

    LoggerService.logMulti('✅ All models loaded');
    this.logMemory();
  }

  /**
   * Get ID Detection Model
   */
  public getIDModel(): any | null {
    return this.idModelReady ? this.idDetectionModel : null;
  }

  /**
   * Get Face Detection Model
   */
  public getFaceDetector(): faceDetection.FaceDetector | null {
    return this.faceModelReady ? this.faceDetector : null;
  }

  /**
   * Check if ID model is ready
   */
  public isIDModelReady(): boolean {
    return this.idModelReady;
  }

  /**
   * Check if face model is ready
   */
  public isFaceModelReady(): boolean {
    return this.faceModelReady;
  }

  /**
   * Check if backend is ready
   */
  public isBackendReady(): boolean {
    return this.backendReady;
  }

  /**
   * Legacy method - backward compatible
   */
  public isReady(): boolean {
    return this.isIDModelReady();
  }

  /**
   * Detect faces using the loaded face detection model
   */
  public async detectFaces(input: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement): Promise<faceDetection.Face[]> {
    if (!this.faceModelReady || !this.faceDetector) {
      throw new Error('Face detection model not ready. Call loadFaceDetectionModel() first.');
    }

    try {
      const faces = await this.faceDetector.estimateFaces(input, {
        flipHorizontal: false,
      });

      return faces;
    } catch (error) {
      LoggerService.warn('Face detection error:', error);
      return [];
    }
  }

  /**
   * Log current memory usage
   */
  private logMemory(): void {
    const memory = tf.memory();
    LoggerService.logMulti(`Memory: ${memory.numTensors} tensors, ${(memory.numBytes / 1024 / 1024).toFixed(2)} MB`);
  }

  /**
   * Get current backend name
   */
  public getBackend(): string {
    return tf.getBackend();
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    LoggerService.logMulti('Disposing models...');

    this.faceDetector?.dispose();
    this.faceDetector = null;

    tf.disposeVariables();

    this.idModelReady = false;
    this.faceModelReady = false;
    this.backendReady = false;

    this.logMemory();
  }
}

export const modelLoadingService = new ModelLoadingService();
